import { useTenant } from "../contexts/TenantContext";
import { languages } from "../lib/i18n";
import { Globe, ChevronDown } from "lucide-react";
import { useState, useRef, useEffect } from "react";

// Compact dropdown variant of the language switcher — frees up space on
// the landing page header so we can fit a Login button next to it.
export default function LangSwitch({ compact }) {
    const { lang, setLang } = useTenant();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onClickOut = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        window.addEventListener("click", onClickOut);
        return () => window.removeEventListener("click", onClickOut);
    }, [open]);

    const current = languages.find((l) => l.code === lang) || languages[0];

    if (!compact) {
        // Legacy non-compact pill bar (still used on a couple of marketing
        // sections that have plenty of horizontal space).
        return (
            <div className="inline-flex items-center gap-1 bg-muted rounded-full px-1 py-1" data-testid="lang-switch">
                <Globe size={14} className="ml-2 text-muted-foreground" />
                {languages.map((l) => (
                    <button
                        key={l.code}
                        data-testid={`lang-${l.code}`}
                        onClick={() => setLang(l.code)}
                        className={`text-xs font-semibold px-2.5 py-1 rounded-full transition ${lang === l.code ? "bg-foreground text-background" : "text-muted-foreground"}`}
                    >{l.label}</button>
                ))}
            </div>
        );
    }

    return (
        <div className="relative" ref={ref} data-testid="lang-switch">
            <button
                onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
                data-testid="lang-dropdown-trigger"
                className="inline-flex items-center gap-1 bg-muted rounded-full px-3 py-1.5 text-xs font-semibold hover:bg-muted/80 transition"
            >
                <Globe size={12} className="text-muted-foreground" />
                <span>{current.label}</span>
                <ChevronDown size={11} className={`text-muted-foreground transition ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="absolute right-0 mt-2 w-32 rounded-xl bg-card border border-border shadow-lg overflow-hidden z-50" data-testid="lang-dropdown-menu">
                    {languages.map((l) => (
                        <button
                            key={l.code}
                            data-testid={`lang-${l.code}`}
                            onClick={() => { setLang(l.code); setOpen(false); }}
                            className={`w-full text-left px-3 py-2 text-xs font-semibold transition ${lang === l.code ? "bg-primary/10 text-primary" : "hover:bg-muted"}`}
                        >{l.label}</button>
                    ))}
                </div>
            )}
        </div>
    );
}
